import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const FilmDetails = () => {
    const { film_id } = useParams(); // Get the film ID from the URL
    const [film, setFilm] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [hoveredRating, setHoveredRating] = useState({}); // Initialize as an empty object
    const [currentRatings, setCurrentRatings] = useState({}); // Track current ratings per review
    const navigate = useNavigate();
  
    useEffect(() => {
      const fetchFilmDetails = async () => {
        try {
          const token = localStorage.getItem('token'); // Retrieve the JWT token from localStorage
    
          const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/films/${film_id}`, {
            headers: {
              Authorization: `Bearer ${token}`, // Include the token in the Authorization header
            },
          });
    
          setFilm(response.data);
    
          // Initialize current ratings for each review
          const initialRatings = {};
          response.data.reviews.forEach((review) => {
            initialRatings[review.id] = review.current_user_rating || 0; // Use existing rating if available
          });
          setCurrentRatings(initialRatings);
        } catch (err) {
          console.error('Error fetching film details:', err);
          setError(err.response?.data?.error || 'Failed to fetch film details.');
        }
      };
    
      fetchFilmDetails();
    }, [film_id]);

  const handleRateReview = async (reviewId, rating) => {
    try {
      const token = localStorage.getItem('token'); // JWT token for authentication
      const userId = localStorage.getItem('user_id'); // Get the user ID from localStorage

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/reviews/${reviewId}/rate`,
        { user_id: userId, rating },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Rating submitted successfully:', response.data);
      setSuccess(response.data.message);
      setError('');

      // Update the current rating for the review
      setCurrentRatings((prev) => ({ ...prev, [reviewId]: rating }));

      // Show an alert to the user
      if (response.data.message.includes('updated')) {
        window.alert('You have successfully updated your rating for this feedback!');
      } else {
        window.alert('You have successfully rated this feedback!');
      }

     // Refresh the film details with the token included
    const updatedFilmResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.eatourshorts.com'}/films/${film_id}`, {
      headers: {
        Authorization: `Bearer ${token}`, // Include the token in the Authorization header
      },
    });
      setFilm(updatedFilmResponse.data);
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(err.response?.data?.error || 'Failed to submit rating.');
      setSuccess('');
    }
  };


  const handleMouseEnter = (reviewId, star) => {
    setHoveredRating((prev) => ({ ...prev, [reviewId]: star }));
  };

  const handleMouseLeave = (reviewId) => {
    setHoveredRating((prev) => ({ ...prev, [reviewId]: null }));
  };

  if (error) {
    return <p style={{ color: 'red' }}>{error}</p>;
  }

  if (!film) {
    return <p>Loading film details...</p>;
  }

  return (
    <div
      style={{
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#333',
        backgroundColor: '#f9f9f9', // Light gray background
        minHeight: '100vh', // Full viewport height
      }}
    >
      {/* Film Title */}
      <h1
        style={{
          textAlign: 'center',
          color: '#0056b3', // Blue color for the title
          marginBottom: '20px',
          fontSize: '2.5rem',
        }}
      >
        {film.title}
      </h1>
  
      {/* Film Details */}
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px',
        }}
      >
        <p>
          <strong>Genre:</strong> {film.genre}
        </p>
        <p>
          <strong>Length:</strong> {film.length} minutes
        </p>
        <p>
          <strong>Uploader:</strong> {film.uploader_username}
        </p>
        <p>
          <strong>Uploader's Tokens:</strong> {film.uploader_tokens}
        </p>
        <p>
          <strong>Visibility:</strong>{' '}
          <span
            style={{
              color: film.visibility === 'public' ? 'green' : 'red',
              fontWeight: 'bold',
            }}
          >
            {film.visibility}
          </span>
        </p>
      </div>
  
      {/* Reviews Section */}
      <h2
        style={{
          color: '#333',
          fontSize: '1.8rem',
          marginBottom: '20px',
          textAlign: 'center',
        }}
      >
        Feedbacks
      </h2>
      {film.reviews.length > 0 ? (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
          }}
        >
          {film.reviews.map((review) => (
            <li
              key={review.id}
              style={{
                backgroundColor: '#ffffff',
                padding: '15px',
                borderRadius: '10px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <p>
                <strong>Reviewer:</strong> {review.reviewer_username}
              </p>
              <div
                style={{
                  margin: '10px 0',
                  fontStyle: 'italic',
                  color: '#555',
                }}
              >    {review.review.split('\n').map((line, index) => (
                <p key={index} style={{ margin: '5px 0' }}>
                  {line}
                </p>
              ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '10px',
                }}
              >
                <label
                  style={{
                    marginRight: '10px',
                    fontWeight: 'bold',
                    color: '#333',
                  }}
                >
                  Rate this feedback:
                </label>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    onMouseEnter={() => handleMouseEnter(review.id, star)} // Highlight stars on hover
                    onMouseLeave={() => handleMouseLeave(review.id)} // Remove highlight on mouse leave
                    onClick={() => handleRateReview(review.id, star)} // Submit rating on click
                    style={{
                      cursor: 'pointer',
                      fontSize: '24px',
                      color:
                        star <=
                        (hoveredRating[review.id] || currentRatings[review.id] || 0)
                          ? 'gold'
                          : 'gray', // Highlight stars
                      marginRight: '5px',
                      transition: 'color 0.2s', // Smooth transition for star color
                    }}
                  >
                    ★
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p
          style={{
            textAlign: 'center',
            color: '#666',
            fontStyle: 'italic',
          }}
        >
          No feedbacks yet.
        </p>
      )}
    </div>
  );
};


export default FilmDetails;